import React from 'react';

/**
 * Diagonal "DRAFT" watermark to overlay on the document detail / print view
 * until both approval levels are complete. Pure CSS — no z-index fights.
 *
 * Usage:
 *   {status !== 'approved' && <DraftWatermark label={status === 'approved' ? '' : 'DRAFT'} />}
 *
 * Props:
 *   label  — defaults to 'DRAFT'. Pass 'PENDING — LEVEL 2' or similar for nuance.
 *   color  — defaults to subtle gray; pass 'red' for rejected if desired.
 */
export default function DraftWatermark({ label = 'DRAFT', color = 'rgba(180,30,30,0.10)' }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5,
      }}
      data-testid="draft-watermark"
    >
      <div
        style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontWeight: 900,
          fontSize: 'min(22vw, 220pt)',
          color,
          transform: 'rotate(-30deg)',
          letterSpacing: '0.1em',
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}
      >
        {label}
      </div>
    </div>
  );
}

/**
 * Compact list-row status badge — use in PO / Invoice list tables.
 */
export function ApprovalStatusBadge({ status }) {
  const meta = {
    draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800' },
    pending_l1: { label: 'Pending L1', cls: 'bg-orange-100 text-orange-800' },
    pending_l2: { label: 'Pending L2', cls: 'bg-purple-100 text-purple-800' },
    approved: { label: '✓ Approved', cls: 'bg-emerald-100 text-emerald-800' },
  }[status] || { label: status || 'Draft', cls: 'bg-gray-100 text-gray-700' };

  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}
      data-testid={`approval-badge-${status || 'draft'}`}
    >
      {meta.label}
    </span>
  );
}
